/**
 * A camada anti-corrupção do front.
 *
 * Mesmo padrão que a arquitetura já aprovou do outro lado: o cliente do IW
 * em `Anery.Infrastructure/Iw/` tem mapper explícito, e por isso uma mudança
 * do sistema de fora não vaza para dentro. Aqui vale o mesmo — o resto do
 * front nunca vê `CaseListItem`, só `CasoDaFila`.
 *
 * Três normalizações acontecem NESTE ponto, e só nele:
 *
 *   1. Data. Passa por `parseInstante`, que recusa ISO sem offset. É ele que
 *      pegou `new Date('2026-02-30')` devolvendo 2 de março — 30 de fevereiro
 *      vindo de um bug de fuso do backend viraria uma data plausível, dois
 *      dias adiante, e ninguém notaria.
 *   2. Estado. Fora dos 7 do contrato é ERRO explícito. Nunca célula vazia:
 *      uma célula em branco numa fila de operação lê-se como "não tem nada",
 *      e o caso simplesmente some da vista de quem precisa agir.
 *   3. Campo ausente. `id` e `patientId` vazios são erro pela mesma razão.
 *
 * O TypeScript já garante a forma em tempo de compilação. Estas checagens
 * são de RUNTIME de propósito: o `tsc` valida o que o contrato diz, não o
 * que o processo do outro lado de fato mandou.
 */

import type {
  CasePendency,
  CaseDetail,
  CaseExtractedField,
  CaseExtractedFieldsResponse,
  CaseListItem,
  CaseThreadEmail,
  CaseThreadResponse,
  CaseTimelineEvent,
  CaseTimelineResponse,
  CaseWaitingResponse,
  CaseWaitResponse,
  ListaDeCasos,
  ResumoDasFrentesDto,
} from '@anery/api-contracts';

import { ErroDaApi } from '../../../shared/api/erros';
import { DataInvalida, parseInstante } from '../../../shared/dominio/datas';
import { ehSituacaoDoPrazo } from '../../../shared/triagem/tipos';
import type { Trilha } from '../../../shared/ui/tiposDaEsteira';
import {
  ehModalidadeDoCaso,
  ehRazaoDaImplantacao,
  ehEstadoDaPendencia,
  ehItemDaPendencia,
  ehMotivoDaPendencia,
  type CampoExtraidoDoCaso,
  type EsperaDoCaso,
  type EventoDaLinhaDoTempo,
  type PendenciaDoCaso,
  type ResumoDeEsperas,
  type CasoDaFila,
  type FrenteLida,
  type PassoDaVez,
  type ResumoDasFrentes,
  type CasoDetalhado,
  type MensagemDaThread,
} from './tipos';

/**
 * Resposta que não casa com o contrato.
 *
 * É `ErroDaApi` com `origem: 'formato'` para a tela desenhar o mesmo estado
 * de erro de sempre — com código e correlation_id, e SEM "tentar de novo":
 * repetir a requisição devolve exatamente o mesmo corpo inválido.
 *
 * `codigo` em inglês SCREAMING_SNAKE porque é chave (log, métrica);
 * `message` em português porque é o que a pessoa lê.
 */
export class ErroDeContratoDeCaso extends ErroDaApi {
  constructor(
    readonly campo: string,
    motivo: string,
  ) {
    super({
      mensagem: `A API devolveu um caso fora do contrato (${campo}): ${motivo}.`,
      origem: 'formato',
      codigo: 'CASE_CONTRACT_MISMATCH',
    });
    this.name = 'ErroDeContratoDeCaso';
  }
}

function texto(valor: string, campo: string): string {
  if (valor.trim() === '') throw new ErroDeContratoDeCaso(campo, 'veio vazio');
  return valor;
}

function instante(valor: string, campo: string) {
  try {
    return parseInstante(valor);
  } catch (causa) {
    if (causa instanceof DataInvalida) throw new ErroDeContratoDeCaso(campo, causa.message);
    throw causa;
  }
}

/** `CaseWaitingResponse` → `ResumoDeEsperas`; `undefined` (API sem o campo) vira `null`. */
function mapearResumoDeEsperas(waiting: CaseWaitingResponse | undefined): ResumoDeEsperas | null {
  if (waiting === undefined) return null;
  return {
    abertas: waiting.open ?? [],
    totalSegundos: waiting.totalSeconds,
    deTerceiroSegundos: waiting.thirdPartySeconds,
    nossoSegundos: waiting.oursSeconds,
    pausaDeRespostaSegundos: waiting.responsePausedSeconds,
    pausaDeImplantacaoSegundos: waiting.implementationPausedSeconds,
  };
}

/** `CaseWaitResponse` → `EsperaDoCaso`. `id` nulo é a espera da operadora (derivada, sem linha própria). */
function mapearEsperaDoCaso(item: CaseWaitResponse): EsperaDoCaso {
  return {
    id: item.id ?? null,
    tipo: item.kind,
    origem: item.origin,
    inicio: instante(item.start, 'waits.start'),
    fim: item.end == null ? null : instante(item.end, 'waits.end'),
    abertaPorNome: item.openedByName ?? null,
    fechadaPorNome: item.closedByName ?? null,
  };
}

/** `CaseListItem` (fio, inglês) → `CasoDaFila` (domínio do front, pt-BR). */
export function mapearCasoDaFila(item: CaseListItem): CasoDaFila {
  // O estado NÃO é rejeitado aqui, e isso mudou de propósito (TP-043).
  //
  // Antes, estado fora dos 7 do contrato era erro: a lista inteira quebrava
  // alto em vez de mostrar célula vazia. A `BR-006` (`F4`) pede o oposto para
  // o quadro — o Caso com substatus novo APARECE sinalizado, porque escondê-lo
  // faria a tela afirmar que ele não existe, e quem procurasse voltaria a
  // abrir prontuário no IW. Como as duas telas passaram a ler pelo mesmo
  // mapeador, a regra tolerante vence: derrubar a fila inteira por causa de um
  // Caso é pior que mostrar o token cru num cartão.
  //
  // Quem traduz é `rotuloDoEstado`, que cai para o token quando não conhece.

  // O estado do relógio vem do servidor e fora da escada conhecida é ERRO,
  // como o estado do caso: um prazo sem rótulo leria-se como "sem prazo".
  if (!ehSituacaoDoPrazo(item.responseStatus)) {
    throw new ErroDeContratoDeCaso(
      'responseStatus',
      `${JSON.stringify(item.responseStatus)} não é uma situação de prazo conhecida`,
    );
  }

  if (!ehSituacaoDoPrazo(item.implementationStatus)) {
    throw new ErroDeContratoDeCaso(
      'implementationStatus',
      `${JSON.stringify(item.implementationStatus)} não é uma situação de prazo conhecida`,
    );
  }
  // Razão desconhecida é ERRO: sem ela a tela não saberia dizer por que não há prazo.
  if (!ehRazaoDaImplantacao(item.implementationReason)) {
    throw new ErroDeContratoDeCaso(
      'implementationReason',
      `${JSON.stringify(item.implementationReason)} não é uma razão de implantação conhecida`,
    );
  }
  if (item.modality != null && !ehModalidadeDoCaso(item.modality)) {
    throw new ErroDeContratoDeCaso('modality', `${JSON.stringify(item.modality)} não é AD nem ID`);
  }

  return {
    id: texto(item.id, 'id'),
    pacienteId: texto(item.patientId, 'patientId'),
    pacienteNome: item.patientName ?? null,
    estado: item.state,
    criadoEm: instante(item.createdAt, 'createdAt'),
    atualizadoEm: instante(item.updatedAt, 'updatedAt'),
    prazoDeRespostaEm: item.responseDueAt == null ? null : instante(item.responseDueAt, 'responseDueAt'),
    respondidoEm: item.respondedAt == null ? null : instante(item.respondedAt, 'respondedAt'),
    situacaoDaResposta: item.responseStatus,
    modalidade: item.modality ?? null,
    ventilacaoContinua: item.continuousVentilation,
    autorizadoEm: item.authorizedAt == null ? null : instante(item.authorizedAt, 'authorizedAt'),
    horasDeImplantacao: item.implementationSlaHours ?? null,
    prazoDeImplantacaoEm:
      item.implementationDueAt == null ? null : instante(item.implementationDueAt, 'implementationDueAt'),
    situacaoDaImplantacao: item.implementationStatus,
    razaoDaImplantacao: item.implementationReason,
    pacienteProvisorio: item.patientProvisional,
    motivoDoVinculoIw: ehMotivoDaPendencia(item.iwLinkReason) ? item.iwLinkReason : null,
    pendenciasAbertas: item.openPendencies,
    // Item desconhecido é descartado, não derruba a fila: o cartão mostra o
    // que entende e a contagem (`openPendencies`) continua fiel ao servidor.
    itensPendentes: (item.openPendencyItems ?? []).filter(ehItemDaPendencia),
    totalDePendencias: item.pendencyCount,
    // `null` quando a resposta não trouxe o bloco. `curado: false` dentro dele é
    // outra coisa: a operadora não foi curada. Ver `ResumoDasFrentes`.
    frentes: mapearFrentes(item.frentes),
    completude:
      item.completude == null
        ? null
        : {
            nivelUmCompleto: item.completude.nivelUmCompleto,
            nivelDoisCompleto: item.completude.nivelDoisCompleto,
            faltam: item.completude.faltam,
            nivelProvisorio: item.completude.nivelProvisorio,
            cadastroTotal: item.completude.cadastroTotal,
            cadastroPendente: item.completude.cadastroPendente ?? [],
          },
    nucleoId: item.nucleoId ?? null,
    nucleoNome: item.nucleoNome ?? null,
    operadora: item.payerName ?? null,
    entrouNoEstadoEm: item.enteredStateAt == null ? null : instante(item.enteredStateAt, 'enteredStateAt'),
    temDono: item.hasOwner,
    donoNome: item.ownerName ?? null,
    espera: mapearResumoDeEsperas(item.waiting),
  };
}

/** `CasePendency` (fio) → `PendenciaDoCaso`. Item, estado, nível ou motivo desconhecido é ERRO: pendência sem rótulo leria-se como "nada falta". */
export function mapearPendencia(pendencia: CasePendency): PendenciaDoCaso {
  if (!ehItemDaPendencia(pendencia.item)) {
    throw new ErroDeContratoDeCaso('pendencies.item', `${JSON.stringify(pendencia.item)} não é um item conhecido`);
  }
  if (!ehEstadoDaPendencia(pendencia.state)) {
    throw new ErroDeContratoDeCaso('pendencies.state', `${JSON.stringify(pendencia.state)} não é um estado conhecido`);
  }
  if (pendencia.reason != null && !ehMotivoDaPendencia(pendencia.reason)) {
    throw new ErroDeContratoDeCaso('pendencies.reason', `${JSON.stringify(pendencia.reason)} não é um motivo conhecido`);
  }
  if (pendencia.level !== 1 && pendencia.level !== 2) {
    throw new ErroDeContratoDeCaso('pendencies.level', `${String(pendencia.level)} não é um nível (1 ou 2)`);
  }
  return {
    item: pendencia.item,
    nivel: pendencia.level,
    estado: pendencia.state,
    motivo: pendencia.reason ?? null,
    detectadaEm: instante(pendencia.detectedAt, 'pendencies.detectedAt'),
    resolvidaEm: pendencia.resolvedAt == null ? null : instante(pendencia.resolvedAt, 'pendencies.resolvedAt'),
  };
}

/** `CaseDetail` → `CasoDetalhado`: a linha da fila mais os gatilhos; `availableTriggers` nulo vira lista vazia (caso encerrado). */
export function mapearCasoDetalhado(detalhe: CaseDetail): CasoDetalhado {
  return {
    // O detalhe não carrega `pendencyCount` — ele traz a LISTA inteira, que é
    // fonte mais completa. O total sai dela.
    ...mapearCasoDaFila({ ...detalhe, pendencyCount: (detalhe.pendencies ?? []).length }),
    gatilhosDisponiveis: detalhe.availableTriggers ?? [],
    // `pendencies` nulo = nada registrado, como `availableTriggers`.
    pendencias: (detalhe.pendencies ?? []).map(mapearPendencia),
    // `waits` nulo = nenhuma espera registrada (`TP-048`).
    esperas: (detalhe.waits ?? []).map(mapearEsperaDoCaso),
    emailRecebidoEm: detalhe.emailReceivedAt == null ? null : instante(detalhe.emailReceivedAt, 'emailReceivedAt'),
  };
}

/** `CaseTimelineEvent` (fio) → `EventoDaLinhaDoTempo`. `detail` nulo vira objeto vazio — nenhum evento fica sem detalhe. */
export function mapearEventoDaLinhaDoTempo(evento: CaseTimelineEvent): EventoDaLinhaDoTempo {
  return {
    tipo: evento.type ?? '',
    ocorreuEm: instante(evento.occurredAt, 'linhaDoTempo.occurredAt'),
    atorTipo: evento.actorType,
    atorNome: evento.actorName ?? null,
    detalhe: evento.detail ?? {},
  };
}

/** `GET /casos/{id}/linha-do-tempo` devolve ARRAY PURO, do mais antigo ao mais recente — a API já ordena. */
export function mapearLinhaDoTempo(lista: CaseTimelineResponse): readonly EventoDaLinhaDoTempo[] {
  if (!Array.isArray(lista)) {
    throw new ErroDeContratoDeCaso('corpo', 'esperado um array de eventos da linha do tempo');
  }
  return lista.map(mapearEventoDaLinhaDoTempo);
}

/**
 * `GET /casos` devolve ARRAY PURO — não há envelope e não há total.
 *
 * O tipo vem de `ListaDeCasos`, derivado do path no contrato: se a listagem
 * virar um envelope paginado algum dia, esta função quebra no `tsc` em vez
 * de devolver `undefined` em runtime.
 */
export function mapearFilaDeCasos(lista: ListaDeCasos): readonly CasoDaFila[] {
  if (!Array.isArray(lista)) {
    throw new ErroDeContratoDeCaso('corpo', 'esperado um array de casos');
  }
  return lista.map(mapearCasoDaFila);
}

/**
 * `CaseThreadEmail` (fio, inglês) → `MensagemDaThread` (domínio do front,
 * pt-BR) — `TP-023`, `T7`. `subject` nulo vira `assunto: null`, não string
 * vazia: célula "(sem assunto)" é decisão de apresentação, não de contrato.
 */
export function mapearMensagemDaThread(item: CaseThreadEmail): MensagemDaThread {
  return {
    ticketId: texto(item.ticketId, 'ticketId'),
    emailId: texto(item.emailId, 'emailId'),
    assunto: item.subject ?? null,
    sentido: item.direction,
    recebidoEm: instante(item.receivedAt, 'receivedAt'),
  };
}

/**
 * `GET /casos/{id}/thread` devolve ARRAY PURO, como `/casos`. Lista vazia é
 * estado LEGÍTIMO (caso sem ticket apontando) — não passa por aqui como erro.
 */
export function mapearThreadDoCaso(lista: CaseThreadResponse): readonly MensagemDaThread[] {
  if (!Array.isArray(lista)) {
    throw new ErroDeContratoDeCaso('corpo', 'esperado um array de mensagens da thread');
  }
  return lista.map(mapearMensagemDaThread);
}

/**
 * `CaseExtractedField` (fio, inglês) → `CampoExtraidoDoCaso` (domínio do
 * front, pt-BR) — `TP-031`, `T7`. `origem` chega opcional no contrato (campo
 * clínico e leitura de regra sem confiança registrada continuam podendo vir
 * sem ele); vira `null`, mesmo tratamento de `assunto` nulo em
 * `mapearMensagemDaThread`.
 */
export function mapearCampoExtraidoDoCaso(item: CaseExtractedField): CampoExtraidoDoCaso {
  return {
    ticketId: texto(item.ticketId, 'ticketId'),
    objetoId: texto(item.objetoId, 'objetoId'),
    objetoTipo: item.objetoTipo,
    rotuloDeOrigem: item.nomeArquivoOuAssunto ?? null,
    campo: item.campo,
    ordem: item.ordem,
    encontrado: item.encontrado,
    valor: item.valor ?? null,
    valorHumano: item.valorHumano ?? null,
    confianca: item.confianca ?? null,
    limiar: item.limiar ?? null,
    origem: item.origem ?? null,
  };
}

/**
 * `GET /casos/{id}/campos-extraidos` devolve ARRAY PURO, como `/thread`.
 * Lista vazia é estado LEGÍTIMO (caso sem ticket apontando) — não passa por
 * aqui como erro.
 */
export function mapearCamposExtraidosDoCaso(lista: CaseExtractedFieldsResponse): readonly CampoExtraidoDoCaso[] {
  if (!Array.isArray(lista)) {
    throw new ErroDeContratoDeCaso('corpo', 'esperado um array de campos extraídos');
  }
  return lista.map(mapearCampoExtraidoDoCaso);
}

/**
 * Frente com token fora do vocabulário é DESCARTADA, e o resto segue — mesma
 * disciplina de `itensPendentes`: um valor que a tela não entende não derruba
 * o Caso inteiro. As contagens (`prontas`, `total`) continuam as do servidor.
 */
function mapearFrentes(
  bruto: ResumoDasFrentesDto | undefined,
): ResumoDasFrentes | null {
  if (bruto == null) return null;

  return {
    curado: bruto.curado,
    prontas: bruto.prontas,
    total: bruto.total,
    travada: ehTrilha(bruto.travada) ? bruto.travada : null,
    semTravaInterna: bruto.semTravaInterna,
    porta1Aberta: bruto.porta1Aberta,
    porta2Aberta: bruto.porta2Aberta,
    tudoDispensado: bruto.tudoDispensado,
    frentes: (bruto.frentes ?? []).flatMap((f): FrenteLida[] =>
      ehTrilha(f.frente)
        ? [
            {
              frente: f.frente,
              rotulo: f.rotulo ?? f.frente,
              estado: (f.estado ?? 'aberta') as FrenteLida['estado'],
              passosFeitos: f.passosFeitos,
              totalDePassos: f.totalDePassos,
              paradaDesde: instante(f.paradaDesde, 'paradaDesde'),
              prazoEm: f.prazoEm == null ? null : instante(f.prazoEm, 'prazoEm'),
              motivoDaDispensa: f.motivoDaDispensa ?? null,
              travando: f.travando,
              esperando: f.esperando ?? null,
              porQueEspera: f.porQueEspera ?? null,
              passo:
                f.passo == null
                  ? null
                  : {
                      ordem: f.passo.ordem,
                      rotulo: f.passo.rotulo ?? '',
                      gesto: (f.passo.gesto ?? 'confirma') as PassoDaVez['gesto'],
                      responsavel: f.passo.responsavel ?? '',
                      botao: f.passo.botao ?? '',
                      acao: f.passo.acao ?? '',
                      campos: f.passo.campos ?? [],
                      marco: (f.passo.marco ?? null) as PassoDaVez['marco'],
                    },
            },
          ]
        : [],
    ),
  };
}

const TRILHAS_CONHECIDAS: readonly string[] = [
  'avaliacao',
  'documentacao_hospitalar',
  'cadastro',
  'validacao_pad',
  'equipe',
];

function ehTrilha(valor: string | null | undefined): valor is Trilha {
  return valor != null && TRILHAS_CONHECIDAS.includes(valor);
}
